/**
 * Session HUD: compass tape, direction cards, zone chips, messages, speed, status, minimap slot.
 * `update()` runs every frame: transforms per frame, text at <= 2 Hz.
 */
import type { GpsGate, HudMessage, HudState, RenderMode, TrackingState, UiCallbacks, ZoneKind } from '../contracts';
import { debugStore, settingsStore } from '../settings';
import { CompassTape } from './compass';
import { button, guardXrSelect, h, setClass, setHidden, TextSlot, TransformSlot } from './dom';
import {
  cardinal,
  extendedLabel,
  formatAccuracy,
  formatDistanceParts,
  formatSpeed,
  isSpeeding,
  relativeBearing,
  zoneLabel,
} from './format';
import { icon, type IconName } from './icons';

/** Minimum interval between text/number updates (ms) => <= 2 Hz. */
export const TEXT_INTERVAL_MS = 500;

const MODE_LABEL: Record<RenderMode, string> = { xr: 'AR', camera: 'Kamera', sim: 'Simulation' };

export interface HudDeps {
  callbacks: UiCallbacks;
  openSettings(opener: HTMLElement): void;
}

interface DirCard {
  el: HTMLElement;
  label: TextSlot;
  value: TextSlot;
  unit: TextSlot;
  arrow: TransformSlot;
  arrowIcon: SVGSVGElement;
}

function dirCard(side: 'left' | 'right'): DirCard {
  const arrowIcon = icon('arrow');
  const label = h('div', 'uz-dir-label');
  const value = h('span', 'uz-dir-num');
  const unit = h('span', 'uz-unit');
  const el = h('div', `uz-dir uz-dir--${side} uz-panel is-off`, { role: 'group' }, [
    h('div', 'uz-dir-arrow', null, [arrowIcon]),
    h('div', 'uz-dir-text', null, [label, h('div', 'uz-dir-value', null, [value, unit])]),
  ]);
  return {
    el,
    label: new TextSlot(label),
    value: new TextSlot(value),
    unit: new TextSlot(unit),
    arrow: new TransformSlot(arrowIcon, (v) => `rotate(${v.toFixed(1)}deg)`, 0.3),
    arrowIcon,
  };
}

export class Hud {
  readonly el: HTMLElement;
  readonly miniMount: HTMLElement;
  readonly gearBtn: HTMLButtonElement;
  private readonly minimap: HTMLElement;
  private readonly compass = new CompassTape();
  private readonly left = dirCard('left');
  private readonly right = dirCard('right');
  private readonly msg: HTMLElement;
  private readonly msgText: TextSlot;
  private readonly msgAction: HTMLButtonElement;
  private msgIconKind: HudMessage['kind'] | null = null;
  private msgActionId: string | null = null;
  private readonly wait: HTMLElement;
  private readonly waitTitle: TextSlot;
  private readonly waitDetail: TextSlot;
  private readonly raw: HTMLElement;
  private readonly rawBody: HTMLTableSectionElement;
  private readonly gpsPill: HTMLElement;
  private readonly gpsText: TextSlot;
  private readonly modePill: HTMLElement;
  private readonly modeText: TextSlot;
  private readonly speed: HTMLElement;
  private readonly speedValue: TextSlot;
  private readonly limitSign: HTMLElement;
  private readonly limitNum: TextSlot;
  private readonly zoneLine: HTMLElement;
  private readonly zoneLineText: TextSlot;
  private readonly lakeLine: TextSlot;
  private readonly miniCol: HTMLElement;

  // Structural state (changes force an immediate text update).
  private lastText = -Infinity;
  private sMode: RenderMode | null = null;
  private sGate: GpsGate | null = null;
  private sTracking: TrackingState | null = null;
  private sZone: ZoneKind | 'none' | null = null;
  private sExtended: boolean | null = null;
  private sLimitKmh: number | null | undefined = undefined;
  private sHasLimit: boolean | null = null;
  private sHasShore: boolean | null = null;
  private sHasHeading: boolean | null = null;
  private sLakeMsg: string | null | undefined = undefined;
  private sMsgKind: string | null | undefined = undefined;
  private sMsgText: string | null | undefined = undefined;
  private sMsgAction: string | null | undefined = undefined;
  private sRaw: boolean | null = null;
  private sSpeeding: boolean | null = null;
  private forceText = true;

  private minimapWanted = true;
  private mapOpen = false;
  private readonly unsubs: (() => void)[] = [];

  constructor(deps: HudDeps) {
    const { callbacks } = deps;

    // Top: exit, compass, gear, direction cards
    const exitBtn = button('uz-iconbtn uz-exit', [icon('exit')], () => callbacks.onExit(), { 'aria-label': 'Beenden' });
    this.gearBtn = button('uz-iconbtn uz-gear', [icon('gear')], () => deps.openSettings(this.gearBtn), {
      'aria-label': 'Einstellungen',
    });
    const topgrid = h('div', 'uz-topgrid', null, [exitBtn, this.compass.el, this.gearBtn, this.left.el, this.right.el]);

    // Centre stack
    const msgTextEl = h('span', 'uz-msg-text');
    this.msgText = new TextSlot(msgTextEl);
    this.msgAction = button('uz-msg-action', '', () => {
      if (this.msgActionId) callbacks.onAction(this.msgActionId);
    });
    this.msg = h('div', 'uz-msg', { role: 'alert', hidden: true }, [msgTextEl, this.msgAction]);

    const waitTitle = h('span', 'uz-wait-title');
    const waitDetail = h('span', 'uz-wait-detail');
    this.wait = h('div', 'uz-wait', { hidden: true }, [icon('gps'), h('div', null, null, [waitTitle, waitDetail])]);
    this.waitTitle = new TextSlot(waitTitle);
    this.waitDetail = new TextSlot(waitDetail);

    this.rawBody = h('tbody');
    this.raw = h('div', 'uz-raw', { hidden: true, 'aria-label': 'Rohwerte' }, [h('table', null, null, [this.rawBody])]);

    const center = h('div', 'uz-center', null, [this.msg, this.wait]);
    const top = h('div', 'uz-hud-top', null, [topgrid, center, this.raw]);

    // Bottom: minimap + GPS accuracy (left), status badge (right, only shown on a problem),
    // combined speed/limit/zone box (centre).
    this.miniMount = h('div', 'uz-minimap-mount');
    this.minimap = h('div', 'uz-minimap', { role: 'button', tabindex: 0, 'aria-label': 'Karte öffnen' }, [
      this.miniMount,
      h('span', 'uz-minimap-badge', null, [icon('expand')]),
    ]);
    this.minimap.addEventListener('click', () => callbacks.onOpenMap());
    this.minimap.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        callbacks.onOpenMap();
      }
    });

    const gpsTextEl = h('span');
    this.gpsPill = h('div', 'uz-pill uz-pill-gps', { 'data-gate': 'waiting' }, [icon('gps'), gpsTextEl]);
    this.gpsText = new TextSlot(gpsTextEl);
    this.miniCol = h('div', 'uz-mini-col', null, [this.minimap, this.gpsPill]);

    this.modePill = h('div', 'uz-pill uz-pill-mode', { hidden: true });
    this.modeText = new TextSlot(this.modePill);
    const status = h('div', 'uz-status', null, [this.modePill]);

    const limitNum = h('span', 'uz-limit-num');
    this.limitSign = h('div', 'uz-limit', { role: 'img', hidden: true }, [h('span', 'uz-limit-max', null, ['max']), limitNum]);
    this.limitNum = new TextSlot(limitNum);
    const speedValueEl = h('span', 'uz-speed-value', null, ['–.–']);
    this.speedValue = new TextSlot(speedValueEl);
    const zoneLineEl = h('div', 'uz-zone-label');
    this.zoneLine = zoneLineEl;
    this.zoneLineText = new TextSlot(zoneLineEl);
    const lakeLineEl = h('div', 'uz-lake-label', { hidden: true });
    this.lakeLine = new TextSlot(lakeLineEl);
    const infoCol = h('div', 'uz-speed-info', null, [zoneLineEl, lakeLineEl]);
    this.speed = h('div', 'uz-speed uz-panel no-limit', { role: 'group', 'aria-label': 'Geschwindigkeit und Zone' }, [
      this.limitSign,
      h('div', 'uz-speed-num', null, [speedValueEl, h('span', 'uz-speed-unit', null, ['km/h'])]),
      infoCol,
    ]);
    const bottom = h('div', 'uz-hud-bottom', null, [this.miniCol, status, this.speed]);

    this.el = h('div', 'uz-hud', { hidden: true }, [top, bottom]);
    for (const el of [this.el, top, bottom, this.minimap, this.msg, exitBtn, this.gearBtn]) guardXrSelect(el);

    this.miniCol.addEventListener('transitionend', (e) => {
      if (e.target !== this.miniCol || e.propertyName !== 'opacity') return;
      if (this.miniCol.classList.contains('is-hidden')) setHidden(this.miniCol, true);
    });

    this.unsubs.push(
      settingsStore.subscribe((s) => {
        this.minimapWanted = s.showMinimap;
        this.syncMinimap();
      }),
      debugStore.subscribe(() => {
        this.forceText = true;
      }),
    );
    this.minimapWanted = settingsStore.get().showMinimap;
    this.syncMinimap();
  }

  dispose(): void {
    this.unsubs.forEach((u) => u());
  }

  setMapOpen(open: boolean): void {
    this.mapOpen = open;
    this.syncMinimap();
  }

  private syncMinimap(): void {
    // Fade+scale via CSS transition; `hidden` (display:none) is only applied once the
    // fade-out has finished (see the transitionend listener below), so it can animate.
    const hide = !this.minimapWanted || this.mapOpen;
    if (!hide) setHidden(this.miniCol, false);
    setClass(this.miniCol, 'is-hidden', hide);
    if (hide && matchMedia('(prefers-reduced-motion: reduce)').matches) setHidden(this.miniCol, true);
  }

  /** The minimap box (for the open/close grow animation). */
  get minimapEl(): HTMLElement {
    return this.minimap;
  }

  /** Called every animation frame. */
  update(s: HudState, now: number): void {
    const zone = s.zone;
    const heading = s.headingDeg;
    const limit = zone && (zone.zone === 'inner' || zone.zone === 'outer' || zone.zone === 'open') ? zone.limit : null;
    const shore = zone && zone.zone !== 'land' && zone.zone !== 'nodata' ? zone.shore : null;

    // Per frame: transforms only.
    this.compass.setHeading(heading, limit ? limit.bearing : null, shore ? shore.bearing : null);
    if (heading != null) {
      if (limit) this.left.arrow.set(relativeBearing(limit.bearing, heading));
      if (shore) this.right.arrow.set(relativeBearing(shore.bearing, heading));
    }

    // Structural changes force a text update; otherwise at most every TEXT_INTERVAL_MS.
    const debug = debugStore.get();
    const rawOn = debug.enabled && debug.showRaw && s.debugRows != null;
    const zoneKey: ZoneKind | 'none' = zone ? zone.zone : 'none';
    const structural =
      this.forceText ||
      s.mode !== this.sMode ||
      s.gpsGate !== this.sGate ||
      s.tracking !== this.sTracking ||
      zoneKey !== this.sZone ||
      (zone?.extended ?? false) !== this.sExtended ||
      (zone?.speedLimitKmh ?? null) !== this.sLimitKmh ||
      (limit != null) !== this.sHasLimit ||
      (shore != null) !== this.sHasShore ||
      (heading != null) !== this.sHasHeading ||
      s.lakeMessage !== this.sLakeMsg ||
      (s.message?.kind ?? null) !== this.sMsgKind ||
      (s.message?.text ?? null) !== this.sMsgText ||
      (s.message?.action?.id ?? null) !== this.sMsgAction ||
      rawOn !== this.sRaw;
    if (!structural && now - this.lastText < TEXT_INTERVAL_MS) return;
    this.lastText = now;
    this.forceText = false;
    this.sMode = s.mode;
    this.sGate = s.gpsGate;
    this.sTracking = s.tracking;
    this.sZone = zoneKey;
    this.sExtended = zone?.extended ?? false;
    this.sLimitKmh = zone?.speedLimitKmh ?? null;
    this.sHasLimit = limit != null;
    this.sHasShore = shore != null;
    this.sHasHeading = heading != null;
    this.sLakeMsg = s.lakeMessage;
    this.sMsgKind = s.message?.kind ?? null;
    this.sMsgText = s.message?.text ?? null;
    this.sMsgAction = s.message?.action?.id ?? null;
    this.sRaw = rawOn;

    this.renderText(s, limit, shore, isSpeeding(s.speedKmh, zone?.speedLimitKmh ?? null), rawOn);
  }

  private renderText(
    s: HudState,
    limit: { distance: number; bearing: number } | null,
    shore: { distance: number; bearing: number } | null,
    speeding: boolean,
    rawOn: boolean,
  ): void {
    const zone = s.zone;
    const heading = s.headingDeg;
    this.compass.setReadout(heading);
    setClass(this.el, 'is-waiting', s.gpsGate === 'waiting');

    // Direction cards
    const inZone = zone?.zone === 'inner' || zone?.zone === 'outer';
    this.renderCard(this.left, limit, inZone ? 'Hinaus' : 'Uferzone in', heading);
    this.renderCard(this.right, shore, 'Ufer', heading);

    // Zone / lake info (folded into the bottom speed box: coloured zone label + grey lake line).
    if (zone) {
      this.zoneLine.dataset.zone = zone.zone;
      setClass(this.zoneLine, 'is-extended', !!zone.extended);
      this.zoneLineText.set(zoneLabel(zone.zone));
      let lakeText: string;
      if (zone.zone === 'nodata') lakeText = `${zone.lakeName} · ausserhalb Kanton Luzern`;
      else if (zone.extended) lakeText = `${zone.lakeName} · ${extendedLabel(zone.extendedNote)}`;
      else lakeText = zone.lakeName;
      this.lakeLine.set(lakeText);
      setHidden(this.lakeLine.el, false);
      setHidden(this.zoneLine, false);
    } else {
      delete this.zoneLine.dataset.zone;
      setClass(this.zoneLine, 'is-extended', false);
      this.zoneLineText.set(s.lakeMessage ?? '');
      setHidden(this.zoneLine, !s.lakeMessage);
      setHidden(this.lakeLine.el, true);
    }

    // Message banner
    const m = s.message;
    if (m) {
      if (m.kind !== this.msgIconKind) {
        this.msgIconKind = m.kind;
        this.msg.dataset.kind = m.kind;
        this.msg.querySelector('.uz-icon')?.remove();
        const name: IconName = m.kind === 'info' ? 'info' : 'warn';
        this.msg.prepend(icon(name));
      }
      this.msgText.set(m.text);
      this.msgActionId = m.action?.id ?? null;
      setHidden(this.msgAction, !m.action);
      if (m.action && this.msgAction.textContent !== m.action.label) this.msgAction.textContent = m.action.label;
      setHidden(this.msg, false);
    } else {
      this.msgActionId = null;
      setHidden(this.msg, true);
    }

    // GPS waiting panel
    const waiting = s.gpsGate === 'waiting';
    setHidden(this.wait, !waiting);
    if (waiting) {
      const thr = s.gpsThresholdM;
      const target = `(Ziel ≤ ${Number.isInteger(thr) ? thr : thr.toFixed(1)} m)`;
      if (s.gpsAccuracy == null) {
        this.waitTitle.set('Warte auf GPS-Signal …');
        this.waitDetail.set(target);
      } else {
        this.waitTitle.set('Warte auf genaues GPS: ');
        this.waitDetail.set(`${formatAccuracy(s.gpsAccuracy)} ${target}`);
      }
    }

    // Status pills
    this.gpsPill.dataset.gate = s.gpsGate;
    const gpsPrefix = s.gpsGate === 'degraded' ? 'GPS ungenau ' : 'GPS ';
    this.gpsText.set(`${gpsPrefix}${formatAccuracy(s.gpsAccuracy)}`);
    // Only shown when tracking actually has a problem; otherwise it would just say "AR" /
    // "Simulation" all the time, which is not useful.
    let modeLabel = MODE_LABEL[s.mode];
    const trackWarn = s.tracking !== 'ok';
    if (trackWarn) modeLabel += s.tracking === 'lost' ? ' · Tracking verloren' : ' · Tracking eingeschränkt';
    this.modeText.set(modeLabel);
    setClass(this.modePill, 'is-warn', trackWarn);
    setHidden(this.modePill, !trackWarn);

    // Speed
    const limitKmh = zone?.speedLimitKmh ?? null;
    const speedText = formatSpeed(s.speedKmh);
    this.speedValue.set(speedText);
    setHidden(this.limitSign, limitKmh == null);
    setClass(this.speed, 'no-limit', limitKmh == null);
    if (limitKmh != null) {
      this.limitNum.set(String(limitKmh));
      this.limitSign.setAttribute('aria-label', `Höchstgeschwindigkeit ${limitKmh} km/h`);
    }
    if (speeding !== this.sSpeeding) {
      this.sSpeeding = speeding;
      setClass(this.speed, 'is-speeding', speeding);
    }
    const speedLabel = `Geschwindigkeit ${speedText} km/h${speeding ? ', Tempolimit überschritten' : ''}`;
    if (this.speed.getAttribute('aria-label') !== speedLabel) this.speed.setAttribute('aria-label', speedLabel);

    // Raw debug table
    setHidden(this.raw, !rawOn);
    if (rawOn && s.debugRows) this.renderRaw(s.debugRows);
  }

  private renderCard(card: DirCard, target: { distance: number; bearing: number } | null, label: string, heading: number | null): void {
    setClass(card.el, 'is-off', target == null);
    if (!target) return;
    const noHead = heading == null;
    setClass(card.el, 'is-nohead', noHead);
    if (noHead) card.arrow.set(0);
    card.label.set(noHead ? `${label} · ${cardinal(target.bearing)}` : label);
    const d = formatDistanceParts(target.distance);
    card.value.set(d.value);
    card.unit.set(d.unit);
  }

  private renderRaw(rows: [string, string][]): void {
    const body = this.rawBody;
    while (body.rows.length > rows.length) body.deleteRow(-1);
    for (let i = 0; i < rows.length; i++) {
      let tr = body.rows[i];
      if (!tr) {
        tr = body.insertRow();
        tr.append(h('th'), h('td'));
      }
      const [k, v] = rows[i];
      const th = tr.cells[0];
      const td = tr.cells[1];
      if (th.textContent !== k) th.textContent = k;
      if (td.textContent !== v) td.textContent = v;
    }
  }
}
